import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useParams } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

/* Existing screens */
import Login from './screens/Login'
import UserList from './screens/UserList'
import AddRole from './screens/addRole'

import Roles from './screens/Roles'
import UserByID from './screens/UserById'
import Dashboard from './screens/Dashboard'
import RoleByID from './screens/RoleById'

/* NEW screens */
import LoanForm from './screens/LoanForm'
import LoanList from './screens/LoanList'
import MerchantCreate from './screens/MerchantCreate'
import MerchantDetail from './screens/MerchantDetail'
import MerchantDashboard from './screens/MerchantDashboard'
import QrPool from './screens/QrPool'
import FormGate from './screens/FormGate'
import LoanTypeList from './screens/LoanTypeList'
import AppBuildManager from './screens/AppBuildManager'
import CallerForm from './screens/CallerForm'
import CallerList from './screens/CallerList'
import CallerDetail from './screens/CallerDetail'
import CallerDashboard from './screens/CallerDashboard'
import ProspectList from './screens/ProspectList'
import DailyCallForm from './screens/DailyCallForm'
import UniversalPage from './screens/UniversalPage'
import ProductMaster from './screens/ProductMaster'
import AgentProgramOverview from './screens/AgentProgramOverview'
import AgentProgramDashboard from './screens/AgentProgramDashboard'
import AgentProgramLeads from './screens/AgentProgramLeads'
import AgentProgramConfig from './screens/AgentProgramConfig'
import AgentKycVerification from './screens/AgentKycVerification'
import AgentPayoutRequests from './screens/AgentPayoutRequests'
import AgentWallet from './screens/AgentWallet'
import AuditLog from './screens/AuditLog'
import Payment from './screens/Payment'

/* Shell */
import SideBar from './components/SideBar'
import Container from './components/Container'

import './assests/styles.css'
import './App.css'
import './assests/scripts'
import 'bootstrap/dist/css/bootstrap.min.css'
import { logout } from './actions/userActions'
import { USER_LOGIN_SUCCESS } from './constants/userConstants'

const isMerchantUser = userInfo =>
  userInfo?.role?.name?.toLowerCase() === 'merchant' || !!userInfo?.merchantId

const getAuthenticatedHome = (userInfo, role = {}) => {
  if (isMerchantUser(userInfo)) return '/merchant/dashboard'
  if (role.loanEnquiriesView) return '/admin/enquiries'
  if (role.productMasterView) return '/admin/product-master'
  if (role.paymentView) return '/admin/payments'
  if (role.dashboard || role.settings) return '/dashboard'
  if (role.qrPoolView) return '/admin/qrs'
  if (role.usersView) return '/users'
  if (role.rolesView) return '/roles'
  if (role.auditLogView) return '/admin/audit-logs'
  if (role.merchantPageView) return '/admin/merchants/create'

  return '/not-authorized'
}

const hasAnyPermission = (role, permissions = []) =>
  permissions.length === 0 || permissions.some(permission => !!role?.[permission])

function SessionManager() {
  const dispatch = useDispatch()
  const { userInfo } = useSelector(state => state.userLogin || {})
  const [inactiveMessage, setInactiveMessage] = useState('')
  const [expiryModalOpen, setExpiryModalOpen] = useState(false)
  const [continuing, setContinuing] = useState(false)
  const [continuationError, setContinuationError] = useState('')
  const [expirySeconds, setExpirySeconds] = useState(60)
  const [continuationWindowExpired, setContinuationWindowExpired] = useState(false)

  useEffect(() => {
    if (!userInfo?.tokenExpiresAt) return undefined

    const remaining = new Date(userInfo.tokenExpiresAt).getTime() - Date.now()
    const durationMs = Number(userInfo.sessionDurationSeconds || 0) * 1000
    // Give the user enough time to act before the signed token expires:
    // one minute for normal sessions, or half of very short sessions.
    const warningMs = Math.min(60000, Math.max(15000, durationMs * 0.5 || 30000))
    const showAfter = Math.max(0, remaining - warningMs)

    const timer = window.setTimeout(() => {
      setContinuationError('')
      setContinuationWindowExpired(false)
      setExpiryModalOpen(true)
    }, showAfter)
    return () => window.clearTimeout(timer)
  }, [userInfo?.sessionDurationSeconds, userInfo?.tokenExpiresAt])

  useEffect(() => {
    if (!expiryModalOpen || !userInfo?.tokenExpiresAt) return undefined

    const updateCountdown = () => {
      const seconds = Math.max(
        0,
        Math.ceil((new Date(userInfo.tokenExpiresAt).getTime() - Date.now()) / 1000),
      )
      setExpirySeconds(Math.min(60, seconds))
      if (seconds <= 0) setContinuationWindowExpired(true)
    }

    updateCountdown()
    const interval = window.setInterval(updateCountdown, 1000)
    return () => window.clearInterval(interval)
  }, [expiryModalOpen, userInfo?.tokenExpiresAt])

  useEffect(() => {
    if (userInfo?.token) return
    setExpiryModalOpen(false)
    setContinuationError('')
    setContinuationWindowExpired(false)
  }, [userInfo?.token])

  useEffect(() => {
    if (!userInfo?.token) return undefined

    let sessionFailureHandled = false
    const handleSessionFailure = error => {
      const status = error?.response?.status
      const code = error?.response?.data?.code
      const accountInactive = code === 'ACCOUNT_INACTIVE'
      const sessionInvalid = status === 401 || code === 'AUTH_FAILED' || code === 'SESSION_EXPIRED'
      if ((!accountInactive && !sessionInvalid) || sessionFailureHandled) return
      sessionFailureHandled = true
      setExpiryModalOpen(false)
      setContinuationError('')
      setContinuationWindowExpired(false)
      if (accountInactive) {
        setInactiveMessage(error.response.data.message || 'You do not have access now. Your account is inactive.')
      }
      dispatch(logout())
    }

    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        handleSessionFailure(error)
        return Promise.reject(error)
      },
    )

    const checkAccount = () => axios.get(`${process.env.REACT_APP_API_URL}/users/me`, {
      headers: { Authorization: `Bearer ${userInfo.token}` },
    }).catch(handleSessionFailure)

    const interval = window.setInterval(checkAccount, 10000)
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') checkAccount()
    }
    window.addEventListener('focus', checkAccount)
    document.addEventListener('visibilitychange', checkWhenVisible)

    return () => {
      axios.interceptors.response.eject(interceptor)
      window.clearInterval(interval)
      window.removeEventListener('focus', checkAccount)
      document.removeEventListener('visibilitychange', checkWhenVisible)
    }
  }, [dispatch, userInfo?.token])

  const signOut = () => {
    setExpiryModalOpen(false)
    setContinuationError('')
    setContinuationWindowExpired(false)
    dispatch(logout())
  }

  const continueCurrentSession = async () => {
    if (!userInfo?.token || userInfo.continuationAvailable !== true || continuationWindowExpired || continuing) return
    setContinuing(true)
    setContinuationError('')
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/continue-session`,
        {},
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      )
      const continuedUser = { ...userInfo, ...data }
      localStorage.setItem('userInfo', JSON.stringify(continuedUser))
      dispatch({ type: USER_LOGIN_SUCCESS, payload: continuedUser })
      setExpiryModalOpen(false)
      setContinuationWindowExpired(false)
    } catch (error) {
      if (error?.response?.data?.code === 'ACCOUNT_INACTIVE') return
      const expired = error?.response?.data?.code === 'SESSION_EXPIRED' || error?.response?.status === 401
      setContinuationError(
        expired
          ? 'Your session expired before it could be continued. Please sign out and log in again.'
          : (error.response?.data?.message || 'The session could not be continued. Please sign out and log in again.'),
      )
    } finally {
      setContinuing(false)
    }
  }

  if (inactiveMessage) {
    return (
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
        <div role="alertdialog" aria-modal="true" aria-labelledby="inactive-account-title" className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
          <h2 id="inactive-account-title" className="text-xl font-semibold text-gray-900">Account inactive</h2>
          <p className="mt-3 text-sm text-gray-600">{inactiveMessage}</p>
          <div className="mt-6 flex justify-end">
            <button type="button" onClick={() => setInactiveMessage('')} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
              OK
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!expiryModalOpen || !userInfo?.token) return null

  const canContinue = userInfo.continuationAvailable === true && !continuationWindowExpired
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div role="alertdialog" aria-modal="true" aria-labelledby="session-expiry-title" className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <div className={`absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full border-4 text-sm font-semibold tabular-nums ${expirySeconds > 10 ? 'border-blue-100 text-blue-600' : 'border-red-100 text-red-600'}`} aria-live="polite" aria-label={`${expirySeconds} seconds remaining`}>
          {expirySeconds === 60 ? '1:00' : `0:${String(expirySeconds).padStart(2, '0')}`}
        </div>
        <h2 id="session-expiry-title" className="pr-16 text-xl font-semibold text-gray-900">Session expiring</h2>
        <p className="mt-3 text-sm text-gray-600">
          {canContinue
            ? 'Your session is about to end. You can continue it one time or sign out now.'
            : continuationWindowExpired && userInfo.continuationAvailable === true
              ? 'The time to continue this session has ended. Please sign out and log in again.'
              : 'Your continued session is about to end. Please sign out and log in again.'}
        </p>
        {continuationError && <p role="alert" className="mt-3 text-sm text-red-600">{continuationError}</p>}
        <button
          type="button"
          onClick={signOut}
          disabled={continuing}
          aria-label="Sign out"
          title="Sign out"
          className="mx-auto mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 17l5-5-5-5" />
            <path d="M15 12H3" />
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          </svg>
        </button>
        <div className="mt-4 flex justify-end gap-3">
          <button type="button" onClick={signOut} disabled={continuing} className="rounded-lg border border-red-500 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50">
            Sign Out
          </button>
          {canContinue && (
            <button type="button" onClick={continueCurrentSession} disabled={continuing} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300">
              {continuing ? 'Continuing…' : 'Continue Session'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function RequireAuth() {
  const { userInfo } = useSelector(state => state.userLogin || {})

  if (!userInfo?.token) return <Navigate to="/" replace />

  return <Outlet />
}

function RequirePermission({
  permissions = [],
  merchantOnly = false,
  adminOnly = false,
  allowOwnMerchantQr = false,
  children,
}) {
  const { qrToken } = useParams()
  const { userInfo } = useSelector(state => state.userLogin || {})
  const { getRoleData } = useSelector(state => state.getRoleInfo || {})
  const role = getRoleData || userInfo?.role || {}
  const merchant = isMerchantUser(userInfo)

  if (!userInfo?.token) return <Navigate to="/" replace />
  if (merchantOnly && !merchant) return <Navigate to={getAuthenticatedHome(userInfo, role)} replace />
  if (adminOnly && merchant) return <Navigate to={getAuthenticatedHome(userInfo, role)} replace />

  const ownMerchantQrAllowed =
    allowOwnMerchantQr &&
    merchant &&
    !!userInfo?.merchantQrToken &&
    userInfo.merchantQrToken === qrToken &&
    !!role.qrView

  if (!ownMerchantQrAllowed && !hasAnyPermission(role, permissions)) {
    return <Navigate to={getAuthenticatedHome(userInfo, role)} replace />
  }

  return children
}

function PublicOnlyRoute({ children }) {
  const { userInfo } = useSelector(state => state.userLogin || {})
  const { getRoleData } = useSelector(state => state.getRoleInfo || {})

  if (userInfo?.token) return <Navigate to={getAuthenticatedHome(userInfo, getRoleData || userInfo.role)} replace />

  return children
}

function AdminLayout() {
  return (
    <Container>
      <SideBar />
      <div className="w-full sm:w-4/5 sm:ml-[20%] min-w-0 bg-gray-100 overflow-x-hidden px-3 pt-16 pb-6 sm:px-6 sm:py-10">
        <Outlet />
      </div>
    </Container>
  )
}

function NotAuthorized() {
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg">
        <h1 className="text-xl font-semibold text-gray-800 mb-2">Not authorized</h1>
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Router>
      <SessionManager />
      <ToastContainer />

      <Routes>
        <Route path="/" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
        <Route path="/form/:token" element={<FormGate />} />
        <Route path="/enquiry/:qrToken" element={<LoanForm />} />

        {/* Standalone caller form — accessible directly without admin sidebar */}
        <Route element={<RequireAuth />}>
          <Route
            path="/caller-form"
            element={<RequirePermission permissions={['callerListAdd']}><CallerForm /></RequirePermission>}
          />
        </Route>

        <Route element={<RequireAuth />}>
          <Route element={<AdminLayout />}>
            <Route path="/not-authorized" element={<NotAuthorized />} />
            <Route path="/universal" element={<UniversalPage />} />

            <Route
              path="/dashboard"
              element={<RequirePermission permissions={['dashboard', 'settings']} adminOnly><Dashboard /></RequirePermission>}
            />

            <Route
              path="/users"
              element={<RequirePermission permissions={['usersView']} adminOnly><UserList /></RequirePermission>}
            />
            <Route
              path="/users/:id"
              element={<RequirePermission permissions={['usersView']} adminOnly><UserByID /></RequirePermission>}
            />

            <Route
              path="/roles"
              element={<RequirePermission permissions={['rolesView']} adminOnly><Roles /></RequirePermission>}
            />
            <Route
              path="/admin/audit-logs"
              element={<RequirePermission permissions={['auditLogView']} adminOnly><AuditLog /></RequirePermission>}
            />
            <Route
              path="/roles/add"
              element={<RequirePermission permissions={['rolesAdd']} adminOnly><AddRole /></RequirePermission>}
            />
            <Route
              path="/roles/update/:id"
              element={<RequirePermission permissions={['rolesUpdate']} adminOnly><RoleByID /></RequirePermission>}
            />

            <Route
              path="/admin/enquiries"
              element={<RequirePermission permissions={['loanEnquiriesView']}><LoanList /></RequirePermission>}
            />
            <Route
              path="/admin/enquiries/send-back"
              element={<RequirePermission permissions={['sendBackEnquiriesView']} adminOnly><LoanList sendBack /></RequirePermission>}
            />
            <Route
              path="/admin/payments"
              element={<RequirePermission permissions={['paymentView']} adminOnly><Payment /></RequirePermission>}
              element={<RequirePermission permissions={['sendBackEnquiriesView']}><LoanList sendBack /></RequirePermission>}
            />
            <Route
              path="/merchant/dashboard"
              element={<RequirePermission merchantOnly><MerchantDashboard /></RequirePermission>}
            />
            <Route
              path="/merchant/enquiries"
              element={<RequirePermission permissions={['loanEnquiriesView']} merchantOnly><LoanList title="My Enquiries" /></RequirePermission>}
            />
            <Route
              path="/admin/merchants/create"
              element={<RequirePermission permissions={['merchantPageView']} adminOnly><MerchantCreate /></RequirePermission>}
              element={<RequirePermission permissions={['merchantView']}><MerchantCreate /></RequirePermission>}
            />
            <Route
              path="/admin/merchants/:qrToken"
              element={<RequirePermission permissions={['merchantView']} allowOwnMerchantQr><MerchantDetail /></RequirePermission>}
            />
            <Route
              path="/admin/qrs"
              element={<RequirePermission permissions={['qrPoolView']}><QrPool /></RequirePermission>}
            />
            <Route
              path="/admin/loan-types"
              element={<RequirePermission permissions={['settings']} adminOnly><LoanTypeList /></RequirePermission>}
            />
            <Route
              path="/admin/app-build"
              element={<RequirePermission permissions={['settings']} adminOnly><AppBuildManager /></RequirePermission>}
            />
            <Route
              path="/admin/caller-list"
              element={<RequirePermission permissions={['callerListView']}><CallerList /></RequirePermission>}
            />
            <Route
              path="/admin/caller-list/:id"
              element={<RequirePermission permissions={['callerListView']}><CallerDetail /></RequirePermission>}
            />
            <Route
              path="/admin/caller-dashboard"
              element={<RequirePermission permissions={['callerListView']}><CallerDashboard /></RequirePermission>}
            />
            <Route
              path="/admin/prospects"
              element={<RequirePermission permissions={['prospectsView']}><ProspectList /></RequirePermission>}
            />
            <Route
              path="/admin/daily-call-report"
              element={<RequirePermission permissions={['dailyCallReportView']}><DailyCallForm /></RequirePermission>}
            />
            <Route
              path="/admin/product-master"
              element={<RequirePermission permissions={['productMasterView']}><ProductMaster /></RequirePermission>}
            />
            <Route
              path="/admin/payments"
              element={<RequirePermission permissions={['loanEnquiriesView']}><Payment /></RequirePermission>}
            />
            <Route
              path="/admin/agent-program"
              element={<RequirePermission permissions={['agentProgramViewAll']} adminOnly><AgentProgramOverview /></RequirePermission>}
            />
            <Route
              path="/admin/agent-program/leads"
              element={<RequirePermission permissions={['agentProgramView', 'agentProgramViewAll']} adminOnly><AgentProgramLeads /></RequirePermission>}
            />
            <Route
              path="/admin/agent-program/config"
              element={<RequirePermission permissions={['agentProgramConfigView']} adminOnly><AgentProgramConfig /></RequirePermission>}
            />
            <Route
              path="/admin/agent-program/kyc"
              element={<RequirePermission permissions={['agentProgramKycView']} adminOnly><AgentKycVerification /></RequirePermission>}
            />
            <Route
              path="/admin/agent-program/payout-requests"
              element={<RequirePermission permissions={['agentProgramPayoutRelease']} adminOnly><AgentPayoutRequests /></RequirePermission>}
            />
            <Route
              path="/admin/agent-program/wallet"
              element={<RequirePermission permissions={['agentProgramViewAll']} adminOnly><AgentWallet /></RequirePermission>}
            />
            <Route
              path="/merchant/agent-program/dashboard"
              element={<RequirePermission merchantOnly><AgentProgramDashboard /></RequirePermission>}
            />
            <Route
              path="/merchant/agent-program/leads"
              element={<RequirePermission permissions={['agentProgramView']} merchantOnly><AgentProgramLeads /></RequirePermission>}
            />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}
